// #336 slice 5: a progression rule written into a slot's cells, and the diff
// the builder previews (brief §5.2, §11.2). The preview IS the apply: it diffs
// the draft against applyRule's own output, so the two cannot disagree.
// Executed rounds stay as they are (`lockedThrough`, §5.2.4, §11.3). What the
// rule does not govern stays too: rest, notes, translations (§5.5).
// Keep free of React and any UI import.
import { blankSlot, type CellDraft, type Draft, type IntensityKind, type SetRowDraft, type SlotDraft } from "./programEdit.js";
import { amrapOf, isCellAbsent, kindAt } from "./programEditBuild.js";
import { updateSession, updateSlot } from "./programEditActions.js";
import { absentCell, alignGroupSlots, setCountAt } from "./programEditRoundSets.js";
import {
  generateRounds,
  type GenerateOptions,
  type ProgressionRule,
  type RuleException,
  type RuleField,
  type RuleSet,
} from "./programProgression.js";
import { recognizeRule, type Recognition } from "./programProgressionRecognize.js";
import { resizeRoundTypes } from "./programRoundTypes.js";
import type { RoundType } from "./schemas/programV3.js";

/** A slot by position: day, then slot in the day. */
export type SlotRef = { day: number; slot: number };

/** A stable key for a ref: «day:slot». */
export const slotRefKey = (ref: SlotRef): string => `${ref.day}:${ref.slot}`;

/** The rule a slot follows now. The editor passes its cache (ruleOfSlot), so
 *  a stepper tap does not read every target's rule again. */
export type SlotRecognizer = (slot: SlotDraft, types: readonly RoundType[]) => Recognition;

export type ApplyOptions = GenerateOptions & {
  /** Rounds 0..lockedThrough were executed: never written. */
  lockedThrough?: number;
  /** The first round written. Default 0. */
  from?: number;
  /** Cells left as they are. "hand" (default): the exceptions of the rule
   *  the slot follows now («Le N celle a mano restano»), less the intensities
   *  on another axis when the rule changes axis (handOffAxis). An explicit
   *  list is kept as it is. [] rewrites all. */
  keep?: "hand" | readonly RuleException[];
  /** How "hand" reads the slot's rule. Default recognizeRule. */
  recognize?: SlotRecognizer;
};

/** The last round executed in a program in progress (§5.2.4, §11.3): the
 *  highest round with a session saved (maxWeekIdx), the one in progress
 *  included, else the marker's last round (a marker written before it). -1,
 *  nothing locked: no marker (never run, or a template), or a draft. */
export const lockedThroughOf = (
  draft: Pick<Draft, "status" | "cycleWeeks">,
  progress: { lastWeekIdx: number; maxWeekIdx?: number | undefined } | null | undefined,
): number =>
  !progress || draft.status === "draft"
    ? -1
    : Math.min(progress.maxWeekIdx ?? progress.lastWeekIdx, draft.cycleWeeks - 1);

/** The draft's round types, one per round. */
export const draftRoundTypes = (draft: Draft): RoundType[] =>
  resizeRoundTypes(draft.roundTypes ?? [], draft.cycleWeeks);

/** The cell of `row` at `w`, its axis kept on it when it is not `axis`. */
const cellIn = (row: SetRowDraft, w: number, axis: IntensityKind): CellDraft => {
  const c: CellDraft = { ...row.cells[w]! };
  const k = kindAt(row, w);
  if (k === axis) delete c.kind;
  else c.kind = k;
  return c;
};

/** `base` rewritten with the generated `set`, except the fields in `kept`. */
const writeCell = (
  base: CellDraft,
  set: RuleSet,
  kept: ReadonlySet<RuleField>,
  oldKind: IntensityKind,
  rule: ProgressionRule,
): CellDraft => {
  const c: CellDraft = { ...base };
  delete c.absent;
  if (!kept.has("value")) {
    delete c.kind;
    c.intensityValue = set.value === null ? "" : String(set.value);
  } else if (oldKind !== rule.axis) c.kind = oldKind;
  else delete c.kind;
  if (!kept.has("reps")) {
    if (rule.work === "countdown") {
      c.reps = "0";
      c.workDurationSec = String(set.reps);
    } else {
      c.reps = String(set.reps);
      delete c.workDurationSec;
    }
  }
  if (!kept.has("amrap")) {
    // #355: a rule with no stop of its own leaves the cell's; no AMRAP, no stop.
    if (set.amrap) {
      c.amrap = true;
      if (set.amrapStop) c.amrapStop = { ...set.amrapStop };
    } else {
      delete c.amrap;
      delete c.amrapStop;
    }
  }
  if (!kept.has("range")) {
    if (set.range) {
      c.repsMin = String(set.range.min);
      c.repsMax = String(set.range.max);
    } else {
      delete c.repsMin;
      delete c.repsMax;
    }
  }
  return c;
};

/** The cells by hand `rule` writes over anyway: an intensity typed on an axis
 *  that is not the rule's. When the rule changes axis they go back to it. */
export const handOffAxis = (slot: SlotDraft, hand: readonly RuleException[], axis: IntensityKind): RuleException[] =>
  hand.filter((e) => {
    const row = slot.setRows[e.set];
    return e.field === "value" && row !== undefined && kindAt(row, e.week) !== axis;
  });

/** The rule `slot` follows now, through the caller's recognizer if any. */
export const recognizeWith = (slot: SlotDraft, types: readonly RoundType[], opts: ApplyOptions): Recognition =>
  opts.recognize ? opts.recognize(slot, types) : recognizeRule(slot, types, opts);

const resolveKeep = (slot: SlotDraft, types: readonly RoundType[], opts: ApplyOptions, axis: IntensityKind): readonly RuleException[] => {
  if (Array.isArray(opts.keep)) return opts.keep;
  const rec = recognizeWith(slot, types, opts);
  if (rec.kind !== "rule") return [];
  if (rec.rule.axis === axis) return rec.exceptions;
  const off = new Set(handOffAxis(slot, rec.exceptions, axis));
  return rec.exceptions.filter((e) => !off.has(e));
};

/** `slot` with `rule` written into the rounds it runs in, from `from` on and
 *  after `lockedThrough`. null when it cannot be: the rule changes the reps or
 *  time bound of a row that a round left alone still uses. */
export const applyRuleToSlot = (
  slot: SlotDraft,
  rule: ProgressionRule,
  types: readonly RoundType[],
  opts: ApplyOptions = {},
): SlotDraft | null => {
  const n = slot.inactiveWeeks.length;
  const from = Math.max(opts.from ?? 0, (opts.lockedThrough ?? -1) + 1);
  const writes = (w: number) => w >= from && !slot.inactiveWeeks[w];
  const keep = resolveKeep(slot, types, opts, rule.axis);
  const gen = generateRounds(rule, types, n, opts);
  const old = slot.setRows;

  const written = Array.from({ length: n }, (_, w): CellDraft[] | null => {
    if (!writes(w)) return null;
    const want = gen[w]!;
    const oldCount = setCountAt(slot, w);
    const kept = keep.filter((e) => e.week === w);
    const count = kept.some((e) => e.field === "sets") && oldCount > 0 ? oldCount : want.length;
    const out: CellDraft[] = [];
    for (let i = 0; i < count; i += 1) {
      const row = old[i];
      if (i >= want.length) {
        out.push(cellIn(row!, w, rule.axis));
        continue;
      }
      const prev = out[i - 1];
      const base = row && i < oldCount ? row.cells[w]! : prev ? { ...prev } : { reps: "0", intensityValue: "" };
      const fields = new Set(kept.filter((e) => e.set === i && i < oldCount).map((e) => e.field));
      out.push(writeCell(base, want[i]!, fields, row && i < oldCount ? kindAt(row, w) : rule.axis, rule));
    }
    return out;
  });

  const counts = written.map((cells, w) => cells?.length ?? (slot.inactiveWeeks[w] ? 0 : setCountAt(slot, w)));
  const rowsN = Math.max(1, ...counts);
  const bound = rule.work;
  for (const [r, row] of old.slice(0, rowsN).entries()) {
    const usedElsewhere = row.cells.some((_, w) => !written[w] && !slot.inactiveWeeks[w] && r < counts[w]!);
    if (usedElsewhere && row.workKind !== bound) return null;
  }
  const setRows = Array.from({ length: rowsN }, (_, r): SetRowDraft => {
    const prior = old[r];
    const cells = Array.from({ length: n }, (_cell, w): CellDraft => {
      const mine = written[w];
      if (mine) return mine[r] ?? absentCell();
      if (prior?.cells[w]) return cellIn(prior, w, rule.axis);
      return slot.inactiveWeeks[w] ? { reps: "0", intensityValue: "" } : absentCell();
    });
    const next: SetRowDraft = {
      ...(prior ?? {}),
      intensityKind: rule.axis,
      cells,
      notesPerCell: prior?.notesPerCell ?? Array.from({ length: n }, () => ""),
    };
    if (bound) next.workKind = bound;
    else delete next.workKind;
    return next;
  });
  return { ...slot, setRows };
};

/** The set rows `rule` writes into a new slot of `rounds` rounds. */
export const rowsForRule = (
  rule: ProgressionRule,
  types: readonly RoundType[],
  rounds: number,
  opts: GenerateOptions = {},
): SetRowDraft[] => {
  const empty: SlotDraft = { ...blankSlot(rounds), setRows: [] };
  return applyRuleToSlot(empty, rule, types, { ...opts, keep: [] })?.setRows ?? [];
};

/** The draft with `rule` applied to the slot at `ref`; the same draft when
 *  there is no such slot or applyRuleToSlot refuses. In a superset the
 *  other members take the slot's set count round by round (Q7). */
export const applyRule = (draft: Draft, ref: SlotRef, rule: ProgressionRule, opts: ApplyOptions = {}): Draft => {
  const slot = draft.sessions[ref.day]?.slots[ref.slot];
  if (!slot) return draft;
  const next = applyRuleToSlot(slot, rule, draftRoundTypes(draft), opts);
  if (!next) return draft;
  const written = updateSlot(draft, ref.day, ref.slot, next);
  const gid = slot.group?.id;
  const day = written.sessions[ref.day];
  if (!gid || !day) return written;
  const from = Math.max(opts.from ?? 0, (opts.lockedThrough ?? -1) + 1);
  const slots = alignGroupSlots(day.slots, gid, { leader: ref.slot, from });
  return slots === day.slots ? written : updateSession(written, ref.day, { slots });
};

/** What changes from `before` to `after`: per round, and how many cells. */
export type SlotDiff = { rounds: boolean[]; cells: number };

const cellSig = (slot: SlotDraft, r: number, w: number): string => {
  const row = slot.setRows[r];
  const c = row?.cells[w];
  if (!row || !c || isCellAbsent(row, w) || slot.inactiveWeeks[w]) return "";
  return JSON.stringify([
    c.reps, amrapOf(c), c.amrapStop ?? null, c.intensityValue, kindAt(row, w), c.repsMin ?? "", c.repsMax ?? "",
    row.workKind ?? "", c.workDurationSec ?? "",
  ]);
};

export const diffSlot = (before: SlotDraft, after: SlotDraft): SlotDiff => {
  const n = Math.max(before.inactiveWeeks.length, after.inactiveWeeks.length);
  const rows = Math.max(before.setRows.length, after.setRows.length);
  const rounds: boolean[] = [];
  let cells = 0;
  for (let w = 0; w < n; w += 1) {
    let changed = false;
    for (let r = 0; r < rows; r += 1) {
      if (cellSig(before, r, w) !== cellSig(after, r, w)) {
        changed = true;
        cells += 1;
      }
    }
    rounds.push(changed);
  }
  return { rounds, cells };
};

/** `diffs` follows `refs`; `members` are the superset mates that change
 *  with them (Q7). `cells` counts both. */
export type RulePreview = {
  draft: Draft;
  diffs: SlotDiff[];
  members: Array<{ ref: SlotRef; diff: SlotDiff }>;
  cells: number;
};

/** The rule for the `i`th ref: the one rule, or the list's own (a sheet over
 *  several rows keeps each row's anchor, programProgressionRows). The list is
 *  one rule per ref; a shorter one reuses its last rule, an empty one has none. */
const ruleOf = (rule: ProgressionRule | readonly ProgressionRule[], i: number): ProgressionRule | undefined =>
  "axis" in rule ? rule : rule[Math.min(i, rule.length - 1)];

/** The builder's preview (§5.4b point 4-5): `rule` applied to every slot in
 *  `refs` (a list: one rule per ref), the draft «Applica» writes, and what
 *  changes in each. */
export const previewRule = (
  draft: Draft,
  refs: readonly SlotRef[],
  rule: ProgressionRule | readonly ProgressionRule[],
  opts: ApplyOptions = {},
): RulePreview => {
  const next = refs.reduce((d, ref, i) => {
    const r = ruleOf(rule, i);
    return r ? applyRule(d, ref, r, opts) : d;
  }, draft);
  const diffs = refs.map((ref) => {
    const a = draft.sessions[ref.day]?.slots[ref.slot];
    const b = next.sessions[ref.day]?.slots[ref.slot];
    return a && b ? diffSlot(a, b) : { rounds: [], cells: 0 };
  });
  const members: RulePreview["members"] = [];
  draft.sessions.forEach((s, day) =>
    s.slots.forEach((a, slot) => {
      const b = next.sessions[day]?.slots[slot];
      if (!b || a === b || refs.some((r) => r.day === day && r.slot === slot)) return;
      const diff = diffSlot(a, b);
      if (diff.cells > 0) members.push({ ref: { day, slot }, diff });
    }),
  );
  const cells = [...diffs, ...members.map((m) => m.diff)].reduce((n, d) => n + d.cells, 0);
  return { draft: next, diffs, members, cells };
};

/** «Riallinea»: the slot back on the rule it follows, in round `week` only or,
 *  with no week, in every round. A slot with no rule: the same draft. */
export const realignToRule = (
  draft: Draft,
  ref: SlotRef,
  opts: GenerateOptions & { week?: number; lockedThrough?: number; recognize?: SlotRecognizer } = {},
): Draft => {
  const slot = draft.sessions[ref.day]?.slots[ref.slot];
  if (!slot) return draft;
  const rec = recognizeWith(slot, draftRoundTypes(draft), opts);
  if (rec.kind === "manual") return draft;
  const keep = opts.week === undefined ? [] : rec.exceptions.filter((e) => e.week !== opts.week);
  return applyRule(draft, ref, rec.rule, { ...opts, keep });
};
